import { format, getQuarter, parseISO } from 'date-fns'
import * as React from 'react'
import { Link } from 'react-router-dom'

import { Button, ButtonGroup, Select, Tooltip, TooltipTrigger, useTooltipState } from '../../../components'
import { InsiderView, InstOwnFiler } from '../constants'
import { useInsiderTradingRouter } from '../hooks/useInsiderTradingRouter'
import { getInvestorNameFromFiler } from '../utils'
import Layout from './Layout'
import type { Props as LayoutProps } from './Layout'

interface Props extends Pick<LayoutProps, 'isContainer'> {
  reportEndDates: string[]
  filer?: InstOwnFiler | null
}

export function ManagerDetailsLayout({ children, reportEndDates, filer, isContainer }: React.PropsWithChildren<Props>) {
  const tooltip = useTooltipState({
    placement: 'bottom',
  })
  // This is temporary until select options are changed to use Link without reloadDocument
  const [tempReportEndDate, setTempReportEndDate] = React.useState<string | undefined>()
  const insiderTradingRouter = useInsiderTradingRouter<InsiderView.ManagerDetails | InsiderView.ManagerHoldings>()
  const { getInsiderTradingLink, viewParams } = insiderTradingRouter
  const { isManagerView, isDetailsView, isHoldingsView } = React.useMemo(() => {
    const { view } = viewParams
    const isManagerView = [InsiderView.ManagerDetails, InsiderView.ManagerHoldings].includes(view)
    const isFundView = [InsiderView.FundDetails, InsiderView.FundHoldings].includes(view)
    const isDetailsView = [InsiderView.ManagerDetails, InsiderView.FundDetails].includes(view)
    const isHoldingsView = [InsiderView.ManagerHoldings, InsiderView.FundHoldings].includes(view)
    return { isManagerView, isFundView, isDetailsView, isHoldingsView }
  }, [viewParams])

  const reportEndDateItems = React.useMemo(
    () =>
      reportEndDates.map((reportEndDate) => {
        const parsedReportEndDate = parseISO(reportEndDate)
        return {
          value: reportEndDate,
          label: `${format(parsedReportEndDate, 'MM/dd/yyyy')} (Q${getQuarter(parsedReportEndDate)})`,
          href: getInsiderTradingLink({
            view: viewParams.view,
            params: { ...viewParams, reportEndDate },
          }),
          reloadDocument: true,
        }
      }),
    [reportEndDates, viewParams, getInsiderTradingLink]
  )

  const portfolioHighlightsButton = React.useMemo(() => {
    const buttonLabel = 'Portfolio Highlights'

    if (filer?.hasOwnHoldings === false) {
      return (
        <Button theme="chipOutline" disabled>
          <TooltipTrigger state={tooltip}>{buttonLabel}</TooltipTrigger>
          <Tooltip state={tooltip}>{getInvestorNameFromFiler(filer)} does not manage their own portfolio</Tooltip>
        </Button>
      )
    }

    return (
      <Button
        theme="chipOutline"
        active={isDetailsView}
        as={Link}
        to={getInsiderTradingLink({
          view: isManagerView ? InsiderView.ManagerDetails : InsiderView.FundDetails,
          params: viewParams,
        })}
        reloadDocument
      >
        {buttonLabel}
      </Button>
    )
  }, [tooltip, filer, getInsiderTradingLink, viewParams, isManagerView, isDetailsView])

  return (
    <Layout
      middleContent={
        <ButtonGroup hasDivider={false} hasBorder={false} className="-space-x-px">
          {portfolioHighlightsButton}
          <Button
            as={Link}
            theme="chipOutline"
            active={isHoldingsView}
            to={getInsiderTradingLink({
              view: isManagerView ? InsiderView.ManagerHoldings : InsiderView.FundHoldings,
              params: viewParams,
            })}
            reloadDocument
          >
            Holdings
          </Button>
        </ButtonGroup>
      }
      rightContent={
        <Select
          label={<span className="text-2xs text-muted">Report End Date</span>}
          aria-label="Report End Date"
          value={tempReportEndDate ?? viewParams.reportEndDate ?? reportEndDates[0]}
          items={reportEndDateItems}
          className="ml-2 w-36"
          onChange={({ value }) => setTempReportEndDate(value)}
        />
      }
      isContainer={isContainer}
    >
      {children}
    </Layout>
  )
}
